/**
 * How the reviews section decides what to render, and what "Load more" is
 * counting — pure, so both can be tested without a browser.
 *
 * The section has two shapes (see `ReviewList`): DESKTOP is one flat
 * newest-first column, MOBILE is one horizontal rail per source. They used to
 * share a single "how many rows are shown" counter, and that is what broke the
 * mobile view:
 *
 *   The counter sliced the flat newest-first list to 6, and the mobile branch
 *   then bucketed THAT SLICE by platform. Since the merged list is sorted by
 *   date across every source, the newest 6 came from two or three sources — so
 *   an agency with four connected platforms got four chips and two rails.
 *   Measured on Alpine Adventures (2026-09-23): chips for Google, TripAdvisor,
 *   Facebook and Trustpilot; rails for Google (3) and TripAdvisor (3) only,
 *   while Facebook and Trustpilot had 20 reviews each and rendered none.
 *
 * The fix is to page each shape in its own units. One counter still drives
 * both — `pages`, the number of times "Load more" has been pressed plus one —
 * because the desktop/mobile branch is only known AFTER hydration
 * (`useIsDesktop`), so a counter holding a row count would mean the wrong thing
 * for whichever branch it was not initialised for.
 */
import { type PlatformStats } from "./rating-summary";

/** Rows revealed per press in the FLAT list — desktop, or any single source.
 *  Six fills the column without making the section the longest thing on the
 *  page. */
export const PAGE = 6;

/** Cards revealed per press IN EACH RAIL of the mobile grouped view. Smaller
 *  than `PAGE` because it is paid once per source: four connected platforms
 *  render four rails, so three each is twelve cards on a phone, against six for
 *  the one flat column desktop draws. The rail itself scrolls horizontally, so
 *  this is a DOM-weight budget, not a limit on what a reader can reach without
 *  pressing the button. */
export const RAIL_PAGE = 3;

export interface PageableReview {
  id: string;
  platform: string;
}

export interface ReviewGroup<T> {
  platform: string;
  reviews: T[];
}

export interface PagedReviews<T> {
  /** The flat column — empty in grouped mode. */
  flat: T[];
  /** One entry per source that has reviews — empty in flat mode. */
  groups: ReviewGroup<T>[];
  /** Reviews present but not yet rendered — whether the button shows at all. */
  remaining: number;
  /**
   * How many ONE PRESS reveals, which is what the badge shows.
   *
   * These differ in grouped mode and the difference is the whole point. Four
   * sources at `REVIEWS_PER_SOURCE` leaves 68 held back after the first page,
   * but a press deepens every rail by `RAIL_PAGE` — 12 — so a badge reading 68
   * promised one press for what takes six, and the number walked down
   * 68 → 56 → 44 with the button still there. Flat mode has no gap (`remaining`
   * and `PAGE` are the same unit), so the two shapes were making different
   * promises through one control.
   */
  nextPress: number;
}

/**
 * Bucket reviews by source, in the order the summary panel lists its platforms
 * — so the rails read in the same order as the scores above them. A platform
 * the payload sends reviews for but the summary does not list still gets a
 * bucket, appended after the known ones.
 */
export function groupByPlatform<T extends PageableReview>(
  reviews: readonly T[],
  platforms: readonly PlatformStats[],
): ReviewGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const p of platforms) buckets.set(p.platform, []);
  for (const review of reviews) {
    const bucket = buckets.get(review.platform);
    if (bucket) bucket.push(review);
    else buckets.set(review.platform, [review]);
  }
  return [...buckets.entries()]
    .filter(([, list]) => list.length > 0)
    .map(([platform, list]) => ({ platform, reviews: list }));
}

/**
 * What to render, and how many rows are still held back.
 *
 * `grouped` selects the shape. In grouped mode EVERY source with reviews gets a
 * rail — the grouping runs over the whole visible set, and only each rail's own
 * depth is paged. That is the difference from the old behaviour, where a source
 * could be missing entirely because none of its reviews were recent enough to
 * survive a flat slice taken before the bucketing.
 */
export function pageReviews<T extends PageableReview>(
  visible: readonly T[],
  platforms: readonly PlatformStats[],
  grouped: boolean,
  pages: number,
): PagedReviews<T> {
  if (!grouped) {
    const flat = visible.slice(0, pages * PAGE);
    const remaining = visible.length - flat.length;
    return {
      flat,
      groups: [],
      remaining,
      // One flat page, or whatever is left if that is less.
      nextPress: Math.min(remaining, PAGE),
    };
  }
  const depth = pages * RAIL_PAGE;
  const all = groupByPlatform(visible, platforms);
  const groups = all.map((group) => ({
    platform: group.platform,
    reviews: group.reviews.slice(0, depth),
  }));
  const rendered = groups.reduce((n, group) => n + group.reviews.length, 0);
  // Summed PER RAIL, not `RAIL_PAGE × rails`: a source with two reviews left
  // contributes two, not three, so the badge does not promise rows that are
  // not there.
  const nextPress = all.reduce(
    (n, group) =>
      n + Math.min(RAIL_PAGE, Math.max(0, group.reviews.length - depth)),
    0,
  );
  return {
    flat: [],
    groups,
    remaining: visible.length - rendered,
    nextPress,
  };
}
